import React, { useMemo, useState } from "react";

// Lightweight dual-line SVG chart (no external deps).
// data: [{ date: 'YYYY-MM-DD', gold: number, silver: number }]
const RateChart = ({ data = [], height = 220 }) => {
  const [hover, setHover] = useState(null);
  const W = 640;
  const H = height;
  const padX = 16;
  const padY = 24;

  const { goldPath, silverPath, points, gMin, gMax, sMin, sMax } = useMemo(() => {
    if (!data.length) return { goldPath: "", silverPath: "", points: [] };
    const golds = data.map((d) => d.gold);
    const silvers = data.map((d) => d.silver);
    const gMin = Math.min(...golds), gMax = Math.max(...golds);
    const sMin = Math.min(...silvers), sMax = Math.max(...silvers);
    const innerW = W - padX * 2;
    const innerH = H - padY * 2;
    const x = (i) => padX + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
    const yG = (v) => padY + innerH - ((v - gMin) / (gMax - gMin || 1)) * innerH;
    const yS = (v) => padY + innerH - ((v - sMin) / (sMax - sMin || 1)) * innerH;
    const gp = data.map((d, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${yG(d.gold).toFixed(1)}`).join(" ");
    const sp = data.map((d, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${yS(d.silver).toFixed(1)}`).join(" ");
    const pts = data.map((d, i) => ({ ...d, x: x(i), yG: yG(d.gold), yS: yS(d.silver) }));
    return { goldPath: gp, silverPath: sp, points: pts, gMin, gMax, sMin, sMax };
  }, [data, H]);

  if (!data.length) {
    return <div className="font-body italic text-gray-500 text-sm py-8 text-center">No rate history yet.</div>;
  }

  const first = data[0], last = data[data.length - 1];
  const goldChange = last.gold - first.gold;
  const silverChange = last.silver - first.silver;
  const fmt = (n) => "₹" + n.toLocaleString("en-IN");

  return (
    <div>
      <div className="flex flex-wrap items-center gap-6 mb-4">
        <Legend color="#d4af37" label="Gold /10g" value={fmt(last.gold)} change={goldChange} />
        <Legend color="#c8ccd2" label="Silver /kg" value={fmt(last.silver)} change={silverChange} />
        <span className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground ml-auto">Last {data.length} days</span>
      </div>

      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: H }}
          onMouseLeave={() => setHover(null)}>
          <defs>
            <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#d4af37" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#d4af37" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* grid lines */}
          {[0.25, 0.5, 0.75].map((f) => (
            <line key={f} x1={padX} x2={W - padX} y1={padY + f * (H - padY * 2)} y2={padY + f * (H - padY * 2)}
              stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
          ))}
          {/* gold area */}
          <path d={`${goldPath} L${(W - padX).toFixed(1)},${H - padY} L${padX},${H - padY} Z`} fill="url(#goldFill)" />
          {/* lines */}
          <path d={silverPath} fill="none" stroke="#c8ccd2" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          <path d={goldPath} fill="none" stroke="#d4af37" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
          {/* hover markers */}
          {points.map((p, i) => (
            <g key={i}>
              <rect x={p.x - (W / points.length) / 2} y={0} width={W / points.length} height={H} fill="transparent"
                onMouseEnter={() => setHover(i)} />
              {hover === i && (
                <>
                  <line x1={p.x} x2={p.x} y1={padY} y2={H - padY} stroke="rgba(212,175,55,0.4)" strokeDasharray="3 3" />
                  <circle cx={p.x} cy={p.yG} r="4" fill="#d4af37" stroke="#080b12" strokeWidth="2" />
                  <circle cx={p.x} cy={p.yS} r="4" fill="#c8ccd2" stroke="#080b12" strokeWidth="2" />
                </>
              )}
            </g>
          ))}
        </svg>

        {hover !== null && points[hover] && (
          <div className="absolute -top-2 left-0 pointer-events-none"
            style={{ transform: `translateX(${Math.min(Math.max((points[hover].x / W) * 100, 8), 78)}%)` }}>
            <div className="bg-[#0b0f19] border border-gold/30 px-3 py-2 text-xs shadow-lg">
              <div className="font-ui text-[9px] tracking-wider uppercase text-muted-foreground">{points[hover].date}</div>
              <div className="text-gold font-body">Gold {fmt(points[hover].gold)}</div>
              <div className="text-gray-200 font-body">Silver {fmt(points[hover].silver)}</div>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between mt-2 font-ui text-[9px] tracking-wider text-muted-foreground">
        <span>{first.date}</span>
        <span>{last.date}</span>
      </div>
    </div>
  );
};

const Legend = ({ color, label, value, change }) => {
  const up = change >= 0;
  return (
    <div className="flex items-center gap-2">
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
      <div className="leading-tight">
        <div className="font-ui text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{label}</div>
        <div className="font-display text-lg text-white">
          {value}{" "}
          <span className={`text-xs font-body ${up ? "text-emerald-400" : "text-red-400"}`}>
            {up ? "▲" : "▼"} ₹{Math.abs(change).toLocaleString("en-IN")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default RateChart;
